import { memo, useCallback } from 'react';
import { useInstantSearch } from 'react-instantsearch-hooks-web';

import { HeatMap } from '../HeatMap';
import { HeatMapCountryList } from '../HeatMapCountryList';
import { HeatMapSearch } from '../HeatMapSearch';
import { HeatMapSearchResults as SearchResults } from '../HeatMapSearch/HeatMapSearchResults';
import type { IsoCountryCodes } from '../../map-types';
import { useUpdateNavigationBar } from '../NavigationBar/NavigationBarProvider';
import { HeatMapResultItem } from '../HeatMapSearch/HeatMapResultItem';
import { useDrawerState } from '../../providers/DrawerStateProvider';
import {
  MapContainer,
  MapContent,
  DrawerPanel,
  SearchWrapper,
  ListContent,
  DrawerToggle,
} from '../ExperienceLayout';

import { useHeatMapSelectedLocations } from './HeatMapSelectedLocationsProvider';

type Props = {
  visible?: boolean;
};

const HeatMapSearchResults = () => {
  const { indexUiState, setIndexUiState } = useInstantSearch();
  const { resetNavigationBar } = useUpdateNavigationBar();
  const { setSelectedCountryCode, setShowCountryCard } =
    useHeatMapSelectedLocations();

  const handleSearchResultClick = useCallback(
    (code: IsoCountryCodes) => {
      setIndexUiState({
        ...indexUiState,
        query: undefined,
      });
      setSelectedCountryCode(code);
      setShowCountryCard(true);
      resetNavigationBar();
    },
    [
      indexUiState,
      resetNavigationBar,
      setIndexUiState,
      setSelectedCountryCode,
      setShowCountryCard,
    ],
  );

  return (
    <SearchResults
      resultItemComponent={props => (
        <HeatMapResultItem {...props} onResultClick={handleSearchResultClick} />
      )}
    />
  );
};

export const HeatMapExperience = memo(({ visible = true }: Props) => {
  const { heatmapDrawerOpen: isOpen, toggleHeatmapDrawer: toggle } =
    useDrawerState();

  return (
    <MapContainer $isVisible={visible}>
      <MapContent>
        {visible && (
          <DrawerPanel $isOpen={isOpen}>
            <SearchWrapper data-tour="search-wrapper" $isOpen={isOpen}>
              <HeatMapSearch placeholder="Search by country or status">
                <HeatMapSearchResults />
              </HeatMapSearch>
            </SearchWrapper>
            <ListContent $isOpen={isOpen}>
              <HeatMapCountryList />
            </ListContent>
          </DrawerPanel>
        )}
        {visible && (
          <DrawerToggle $isOpen={isOpen} onClick={toggle}>
            {isOpen ? '‹' : '›'}
          </DrawerToggle>
        )}
        <HeatMap visible={visible} drawerOpen={isOpen} />
      </MapContent>
    </MapContainer>
  );
});

HeatMapExperience.displayName = 'HeatMapExperience';
